import { useState } from "react";
import { Controller, useFormContext, useWatch, type FieldErrors } from "react-hook-form";
import { Search } from "lucide-react";
import { Autocomplete, Button, Input, Switch } from "@marekl/adminer/components";
import { apiErrorMessage } from "@marekl/adminer/api";
import { useT } from "@marekl/adminer/hooks";
import { lookupAres, searchAres, type AresHit } from "../../lib/queries/registry";
import type { BillingDetailsInput } from "../../lib/queries/companies";
import { VatStatusBadge } from "./VatStatusBadge";
import styles from "./BillingDetailsFields.module.css";

interface WithBilling {
  billing_details: BillingDetailsInput;
}

/** Empty billing details, the shape every form starts from. */
export function emptyBillingDetails(): Required<BillingDetailsInput> {
  return {
    business_name: "", ico: "", dic: "", vat_payer: false, bank_account: "",
    phone: "", email: "", street: "", post_code: "", city: "", state: "",
  };
}

/**
 * The fields of one party's billing details, inside a react-hook-form whose
 * values have a `billing_details` object (company and contact forms; the
 * server's field errors use the same paths). The name searches ARES, and
 * the "ARES" button beside the IČO fills everything from it.
 */
export function BillingDetailsFields({ bankAccount = true }: { bankAccount?: boolean }) {
  const { register, setValue, control, formState } = useFormContext<WithBilling>();
  const errors = (formState.errors.billing_details ?? {}) as FieldErrors<BillingDetailsInput>;
  const [name, ico, dic] = useWatch({ control, name: ["billing_details.business_name", "billing_details.ico", "billing_details.dic"] });
  const [aresBusy, setAresBusy] = useState(false);
  const [aresError, setAresError] = useState<string>();
  const t = useT();

  async function fillFromAres(fromIco: string) {
    setAresBusy(true);
    setAresError(undefined);
    try {
      const s = await lookupAres(fromIco);
      const opts = { shouldDirty: true, shouldValidate: true } as const;
      for (const [k, v] of Object.entries(s.billing_details)) {
        if (v !== undefined) setValue(`billing_details.${k as keyof BillingDetailsInput}`, v as never, opts);
      }
    } catch (err) {
      setAresError(apiErrorMessage(err, t));
    } finally {
      setAresBusy(false);
    }
  }

  const nameField = register("billing_details.business_name", { required: "Povinné pole" });

  return (
    <div className={styles.root}>
      <div className={styles.cols}>
        <section className={styles.section}>
          <h2 className={styles.heading}>Identifikace</h2>
      <Autocomplete<AresHit>
        label="Obchodní jméno"
        required
        name={nameField.name}
        ref={nameField.ref}
        onBlur={nameField.onBlur}
        value={name ?? ""}
        onChange={(v) => setValue("billing_details.business_name", v, { shouldDirty: true, shouldValidate: true })}
        loadSuggestions={searchAres}
        minChars={3}
        getSuggestionLabel={(h) => h.business_name}
        getSuggestionKey={(h) => h.ico}
        renderSuggestion={(h) => (
          <span>
            <strong>{h.business_name}</strong> · IČO {h.ico}
            <br />
            <small>{h.address}</small>
          </span>
        )}
        onSelectSuggestion={(h) => void fillFromAres(h.ico)}
        hint="Při psaní hledá v ARES; vybraný subjekt doplní všechny údaje."
        error={errors.business_name?.message}
      />
      <div className={styles.pair}>
        <Input
          label="IČO"
          {...register("billing_details.ico")}
          error={errors.ico?.message ?? aresError}
          suffix={
            <Button
              type="button"
              size="sm"
              variant="primary"
              weight="outline"
              aria-label="Načíst z ARES"
              loading={aresBusy}
              disabled={!ico?.trim()}
              startIcon={<Search size={14} aria-hidden="true" />}
              onClick={() => void fillFromAres(ico ?? "")}
            >
              ARES
            </Button>
          }
        />
        <Input label="DIČ" {...register("billing_details.dic")} error={errors.dic?.message} suffix={<VatStatusBadge dic={dic} />} />
      </div>
      <Controller
        name="billing_details.vat_payer"
        control={control}
        render={({ field }) => <Switch label="Plátce DPH" checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
      />
        </section>
        <section className={styles.section}>
          <h2 className={styles.heading}>Adresa</h2>
          <Input label="Ulice a číslo" {...register("billing_details.street")} error={errors.street?.message} />
          <div className={styles.postCity}>
            <Input label="PSČ" {...register("billing_details.post_code")} error={errors.post_code?.message} />
            <Input label="Obec" {...register("billing_details.city")} error={errors.city?.message} />
          </div>
          <Input label="Stát" {...register("billing_details.state")} error={errors.state?.message} />
        </section>
      </div>
      <section className={styles.section}>
        <h2 className={styles.heading}>Kontakt a platby</h2>
        <div className={styles.contact}>
          <Input label="E-mail" type="email" {...register("billing_details.email")} error={errors.email?.message} />
          <Input label="Telefon" {...register("billing_details.phone")} error={errors.phone?.message} />
          {bankAccount && (
            <Input
              label="Bankovní účet"
              {...register("billing_details.bank_account")}
              error={errors.bank_account?.message}
              hint="Např. 19-2000145399/0800 nebo IBAN; z něj se tvoří QR platba."
            />
          )}
        </div>
      </section>
    </div>
  );
}
